/**
 * SOVRANOVA — Install prompt ("Add to Home Screen")
 *
 * Beta wave, 31 Aug 2026 (David): testers get an app-like install with zero
 * app store — the PWA foundation (manifest + icons + service worker +
 * standalone display) has shipped since the store-prereqs work, so the app IS
 * installable; this sheet is the part that tells people so, styled after the
 * install cards big products use.
 *
 * Behaviour:
 * - Never shows inside an installed app (standalone display / navigator.standalone).
 * - Android / desktop Chrome+Edge: captures `beforeinstallprompt` and offers a
 *   real one-tap Install button.
 * - iOS (no install API by design): shows the share → "Add to Home Screen"
 *   instruction row, mirroring Apple's own flow.
 * - Dismiss is remembered for 14 days (localStorage, per device).
 */
import { useState, useEffect, useRef } from 'react'

const DISMISS_KEY = 'sov_install_dismissed_at'
const DISMISS_DAYS = 14

const isStandalone = () =>
  (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)')?.matches) ||
  (typeof navigator !== 'undefined' && navigator.standalone === true)

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream

const dismissedRecently = () => {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0)
    return at && (Date.now() - at) < DISMISS_DAYS * 24 * 3600 * 1000
  } catch { return false }
}

export function InstallPrompt() {
  const [show, setShow] = useState(false)
  const [mode, setMode] = useState(null)     // 'native' (one-tap) | 'ios' (instructions)
  const deferredRef = useRef(null)

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return

    // Chrome / Edge / Android fire this when the app qualifies for install.
    const onBIP = (e) => {
      e.preventDefault()
      deferredRef.current = e
      setMode('native')
      setShow(true)
    }
    window.addEventListener('beforeinstallprompt', onBIP)

    // iOS never fires beforeinstallprompt — show the instruction sheet after a
    // gentle delay so it doesn't collide with the greeting.
    let t = null
    if (isIOS()) t = setTimeout(() => { setMode('ios'); setShow(true) }, 2500)

    // If the person installs (any path), never show again on this device.
    const onInstalled = () => { setShow(false); remember() }
    window.addEventListener('appinstalled', onInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP)
      window.removeEventListener('appinstalled', onInstalled)
      if (t) clearTimeout(t)
    }
  }, [])

  const remember = () => { try { localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch (_) {} }
  const dismiss = () => { setShow(false); remember() }

  const install = async () => {
    const ev = deferredRef.current
    if (!ev) return dismiss()
    try {
      ev.prompt()
      const choice = await ev.userChoice
      if (choice?.outcome === 'accepted') { setShow(false); remember(); return }
    } catch (_) { /* fall through to dismiss */ }
    dismiss()
  }

  if (!show) return null

  return (
    <div style={p.wrap} role="dialog" aria-label="Install Sovranova">
      <div style={p.card}>
        <button style={p.x} onClick={dismiss} aria-label="Close">×</button>
        <img src="/icons/icon-192.png" alt="" style={p.icon} />
        <p style={p.title}>Install SOVRANOVA</p>
        <p style={p.sub}>
          Add it to your home screen — opens full-screen with one tap, just like an app.
        </p>
        {mode === 'native' ? (
          <button style={p.installBtn} onClick={install}>Install</button>
        ) : (
          <p style={p.iosRow}>
            Tap <span style={p.shareGlyph} aria-label="the Share button">⎋</span> below,
            then <strong>&ldquo;Add to Home Screen&rdquo;</strong>
          </p>
        )}
      </div>
    </div>
  )
}

const p = {
  // Sits ABOVE the always-on crisis bar (fixed bottom, z 2147483000, ~64px
  // clearance zone — see CrisisLifeline.jsx + global.css). David's screenshot
  // 31 Aug: at bottom+14px the bar covered the card's instruction line. The
  // crisis bar keeps the higher z-index on purpose — safety always wins.
  wrap: { position:'fixed', left:0, right:0, bottom:'calc(env(safe-area-inset-bottom, 0px) + 88px)', display:'flex', justifyContent:'center', zIndex:2147482000, padding:'0 14px', pointerEvents:'none' },
  card: { pointerEvents:'auto', position:'relative', width:'100%', maxWidth:420, background:'var(--og-bg, #f5ead8)', borderRadius:'22px', boxShadow:'0 10px 40px rgba(46,43,37,0.35)', padding:'1.2rem 1.3rem 1.1rem', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center', gap:'0.45rem' },
  x: { position:'absolute', top:8, right:12, background:'transparent', border:'none', fontSize:'1.5rem', lineHeight:1, color:'var(--og-n500, #a19786)', cursor:'pointer', padding:'0.2rem' },
  icon: { width:56, height:56, borderRadius:14, boxShadow:'0 2px 10px rgba(46,43,37,0.2)' },
  title: { margin:0, fontFamily:'var(--og-fh, Georgia, serif)', fontSize:'1.15rem', color:'var(--og-a900, #402310)' },
  sub: { margin:0, fontSize:'0.85rem', lineHeight:1.55, color:'var(--og-n700, #645c50)' },
  installBtn: { marginTop:'0.35rem', width:'100%', padding:'0.8rem', background:'var(--og-accent, #c67139)', color:'var(--og-bg, #f5ead8)', border:'none', borderRadius:'999px', fontSize:'0.95rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  iosRow: { margin:'0.35rem 0 0', fontSize:'0.9rem', lineHeight:1.6, color:'var(--og-n800, #474238)', borderTop:'1.5px solid var(--og-n200, #eee7db)', paddingTop:'0.7rem', width:'100%' },
  shareGlyph: { display:'inline-block', fontWeight:700, fontSize:'1.05rem', color:'var(--og-accent, #c67139)' },
}
